import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ButtonVariant } from '@brasshq/ui-kit';
import { BRASS_SUPPORT_EMAIL, BRASS_SUPPORT_PHONE, BRASS_SUPPORT_URL } from 'src/data/constants';
import { accountsPagePath } from 'src/data/pageRoutes';
import { CommonContext } from 'src/modules/common/context';

import { MIGRATION_PAGE_ROUTES } from '../../../data/constants';
import { exportMigrationData } from '../../../functions';
import { useMigrationPreview } from '../../../hooks/useMigrationPreview';
import { getMigrationExportDebounceRemaining, setMigrationExportDebounce } from '../../../storage';
import { getMigrationExportRange, MIGRATION_EXPORT_MAX_MONTHS } from '../../../utils';

import styles from './ExportYourData.module.scss';

const MIGRATION_EXPORT_FORMAT = 'pdf';
const EXPORT_DEBOUNCE_WINDOW_MS = 60 * 1000;

const formatRemainingCooldown = (remainingMs: number) => {
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  if (remainingSeconds <= 60) return `${remainingSeconds}s`;
  const minutes = Math.ceil(remainingSeconds / 60);
  return `${minutes}m`;
};

const ExportYourData: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = React.useContext(CommonContext);
  const { isPreview, search } = useMigrationPreview();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const customerId = profile?.customer?.data?.id || '';
  const exportEmail = profile?.customer?.data?.contact_email || profile?.user?.data?.email || '';
  const customerCreatedAt = profile?.customer?.data?.created_at || '';
  const [remainingCooldownMs, setRemainingCooldownMs] = React.useState(0);
  const isExportDebounced = remainingCooldownMs > 0;

  const exportRange = React.useMemo(
    () => getMigrationExportRange(customerCreatedAt),
    [customerCreatedAt]
  );
  const exportFromDateApi = exportRange.fromDateApi;
  const exportFromDateLabel = exportRange.fromDateLabel;
  const exportEndDate = exportRange.toDateLabel;

  React.useEffect(() => {
    if (!customerId) {
      setRemainingCooldownMs(0);
      return;
    }

    const updateCooldown = () =>
      setRemainingCooldownMs(
        getMigrationExportDebounceRemaining(customerId, EXPORT_DEBOUNCE_WINDOW_MS)
      );

    updateCooldown();
    const interval = window.setInterval(updateCooldown, 1000);
    return () => window.clearInterval(interval);
  }, [customerId]);

  const handleSubmit = async () => {
    if (isPreview) {
      navigate(`${MIGRATION_PAGE_ROUTES.accountMigration}${search}`);
      return;
    }

    if (!customerId || !exportEmail || isExportDebounced) return;
    setIsSubmitting(true);
    const response = await exportMigrationData(customerId, {
      email: exportEmail,
      from_date: exportFromDateApi,
      to_date: exportRange.toDateApi,
      format: MIGRATION_EXPORT_FORMAT
    });
    setIsSubmitting(false);

    if (response) {
      setMigrationExportDebounce(customerId);
      navigate(`${MIGRATION_PAGE_ROUTES.accountMigration}${search}`);
    }
  };

  return (
    <section className={styles.ExportYourData}>
      <div className={styles.ExportYourData_content}>
        <p className={styles.ExportYourData_label}>Step 2</p>
        <h1>Export your account history</h1>
        <div className={styles.ExportYourData_notice}>
          <p>
            Need your full account history? Download statements for each account on the{' '}
            <Link to={accountsPagePath}>Accounts page</Link> before continuing. The migration export
            below only includes the last {MIGRATION_EXPORT_MAX_MONTHS} months of data.
          </p>
        </div>
        <p className={styles.ExportYourData_description}>
          We&apos;ll email a copy of your Brass account statements for the last{' '}
          {MIGRATION_EXPORT_MAX_MONTHS} months (
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{exportFromDateLabel}</span> to{' '}
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{exportEndDate}</span>) to your
          registered email address before we begin the migration.
        </p>
        <p className={styles.ExportYourData_note}>
          If you still need access to your account data after your account closes, reach out to
          support at <a href={`mailto:${BRASS_SUPPORT_EMAIL}`}>{BRASS_SUPPORT_EMAIL}</a> or{' '}
          <a href={`tel:${BRASS_SUPPORT_PHONE}`}>{BRASS_SUPPORT_PHONE}</a>, or visit our{' '}
          <a href={BRASS_SUPPORT_URL} target='_blank' rel='noreferrer'>
            help centre
          </a>{' '}
          and we&apos;ll help you retrieve it.
        </p>
        {isExportDebounced && (
          <p className={styles.ExportYourData_description}>
            Another export was recently requested. Please wait{' '}
            <span style={{ fontWeight: 600 }}>{formatRemainingCooldown(remainingCooldownMs)}</span>{' '}
            before trying again.
          </p>
        )}
        <Button
          type='button'
          variant={ButtonVariant.Primary}
          rounded
          className={styles.ExportYourData_cta}
          loading={isSubmitting}
          disabled={
            !isPreview && (isSubmitting || !customerId || !exportEmail || isExportDebounced)
          }
          onClick={handleSubmit}
        >
          Send to my email
        </Button>
      </div>
    </section>
  );
};

export default ExportYourData;
